import { indicationOptionList } from 'lib/clinic/mapper';
import { useCreateProjectOption } from 'lib/hooks/project/useCreateProjectOption';
import {
  IDefaultOption,
  IImplantFormat,
  IImplantLibrary,
  IOptionInfo,
  IOptionUi,
  ISeqName,
} from 'lib/types/clinic/projectTypes';
import React, { Fragment } from 'react';
import styled from 'styled-components';
import ProjectImplant from './ProjectImplant';
import ProjectOptionCheckbox from './ProjectOptionCheckbox';
import ProjectOptionDropdown from './ProjectOptionDropdown';
import ProjectOptionRadioButton from './ProjectOptionRadioButton';
import ProjectOptionSlider from './ProjectOptionSlider';
import ProjectOptionButton from './ProjectOptionButton';
import CustomText from 'components/common/text/CustomText';
import { Close } from '@material-ui/icons';
import { TValue } from 'lib/types/common';

interface ProjectOptionBasicProps {
  implantType: number;
  implantList: IImplantFormat[] | undefined;
  implantAbutmentMaterialIdx: number | null;
  abutmentList: ISeqName[] | null | undefined;
  implantScanbodyScanIdx: number | null;
  scanbodyList: ISeqName[] | null | undefined;
  optionDefaultInfoList: IOptionInfo | null;
  defaultOption: IDefaultOption;
  isPhotogrammetry: boolean;
  implantLibrary: IImplantLibrary | null;
  isSituScanDisabled?: {
    upperJaw: boolean;
    lowerJaw: boolean;
  };
  pimType?: 'normal' | 'scanbody';
  onCheckChange: (
    type: 'default' | 'advanced',
    optionKey: string,
    value: TValue,
    isPhotogrammetry: boolean,
  ) => void;
  onOptionChange: (type: 'default' | 'advanced', optionKey: string, value: TValue) => void;
  onImplantChange: (value: TValue) => void;
  onAbutmentMaterialChange: (value: TValue) => void;
  onScanbodyScanChange: (value: TValue) => void;
  onLibraryClick: () => void;
  onDeleteItemClick: () => void;
}

const ProjectOptionBasic = ({
  implantType,
  implantList,
  implantAbutmentMaterialIdx,
  abutmentList,
  implantScanbodyScanIdx,
  scanbodyList,
  optionDefaultInfoList,
  defaultOption,
  isPhotogrammetry,
  implantLibrary,
  isSituScanDisabled,
  pimType,
  onCheckChange,
  onOptionChange,
  onImplantChange,
  onAbutmentMaterialChange,
  onScanbodyScanChange,
  onLibraryClick,
  onDeleteItemClick,
}: ProjectOptionBasicProps) => {
  return (
    <StyledProjectOptionBasic>
      {/* Implantlist */}
      {!!implantList && (
        <ProjectImplant
          implantType={implantType}
          implantList={implantList}
          implantAbutmentMaterialIdx={implantAbutmentMaterialIdx}
          abutmentList={abutmentList}
          implantScanbodyScanIdx={implantScanbodyScanIdx}
          scanbodyList={scanbodyList}
          onImplantChange={onImplantChange}
          onAbutmentMaterialChange={onAbutmentMaterialChange}
          onScanbodyScanChange={onScanbodyScanChange}
        />
      )}
      {/* Checkbox */}
      {!!optionDefaultInfoList &&
        indicationOptionList.checkbox.map((groupItem, index) => {
          return (
            <Fragment key={index}>
              {isPhotogrammetry === groupItem.isPhotogrammetry && (
                <ProjectOptionCheckbox
                  type="default"
                  groupItem={groupItem}
                  isPhotogrammetry={isPhotogrammetry}
                  isSituScanDisabled={isSituScanDisabled}
                  optionValues={defaultOption}
                  onCheckChange={onCheckChange}
                />
              )}
              {indicationOptionList.checkbox.length - 1 === index && <StyledMidLine />}
            </Fragment>
          );
        })}
      {/* Dropdown */}
      {!isPhotogrammetry &&
        !!optionDefaultInfoList &&
        !!indicationOptionList.dropdown &&
        !!indicationOptionList.dropdown.find(
          (option: IOptionUi) => option.key in optionDefaultInfoList,
        ) && (
          <>
            <ProjectOptionDropdown
              type="default"
              optionValues={defaultOption}
              optionInfoList={optionDefaultInfoList}
              onOptionChange={onOptionChange}
            />

            <StyledMidLine />
          </>
        )}
      {/* Radio Button */}
      {!isPhotogrammetry &&
        !!optionDefaultInfoList &&
        !!indicationOptionList.radio &&
        !!indicationOptionList.radio.find(
          (option: IOptionUi) => option.key in optionDefaultInfoList,
        ) && (
          <>
            <ProjectOptionRadioButton
              type="default"
              optionValues={defaultOption}
              optionInfoList={optionDefaultInfoList}
              onOptionChange={onOptionChange}
            />
            <StyledMidLine />
          </>
        )}
      {/* Slider */}
      {!isPhotogrammetry &&
        !!optionDefaultInfoList &&
        !!indicationOptionList.slider &&
        !!indicationOptionList.slider.find(
          (option: IOptionUi) => option.key in optionDefaultInfoList,
        ) && (
          <ProjectOptionSlider
            type="default"
            optionValues={defaultOption}
            optionInfoList={optionDefaultInfoList}
            onOptionChange={onOptionChange}
          />
        )}
      {/* Button */}
      {isPhotogrammetry && pimType === 'normal' && (
        <div className="projectOptionBasic__option_button_box">
          <ProjectOptionButton onLibraryClick={onLibraryClick} />
          {!!implantLibrary?.implantLibraryIdx && (
            <div className="projectOptionBasic__library_box">
              <CustomText fontSize={14} fontColor="#888" style={{ lineHeight: '22px' }}>{`${
                !!implantLibrary?.companyName ? implantLibrary?.companyName : ''
              }${
                !!implantLibrary?.libraryDisplayName
                  ? ` / ${implantLibrary?.libraryDisplayName}`
                  : ''
              }${!!implantLibrary?.typeDisplayName ? ` / ${implantLibrary?.typeDisplayName}` : ''}${
                !!implantLibrary?.subtypeDisplayName
                  ? ` / ${implantLibrary?.subtypeDisplayName}`
                  : ''
              }`}</CustomText>
              <div
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                onClick={onDeleteItemClick}
              >
                <Close style={{ fontSize: 24, color: '9EA4BE' }} />
              </div>
            </div>
          )}
        </div>
      )}
    </StyledProjectOptionBasic>
  );
};

const StyledProjectOptionBasic = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  row-gap: 30px;
  .projectOptionBasic__option_button_box {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    .projectOptionBasic__library_box {
      display: flex;
      column-gap: 20px;
      align-items: flex-start;
      justify-content: space-between;
    }
  }
`;

const StyledMidLine = styled.div`
  width: 100%;
  border-bottom: 1px solid #e1e1e7;
`;

export default ProjectOptionBasic;
